import React, { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client';
import { isValidDate } from '../../../utils';
import moment from 'moment';
import { UPDATE_LOAD_FIELD_OP, UPLOAD_IMAGE_TO_LOAD } from '../../../graphql/client/mutations';
import { UploadImageToLoadInput, UploadImageToLoadInputDetails, UpdateLoadFieldOpDetail } from '../../../types/schemaTypes';

interface Suggestion {
    id: number | string;
    name: string;
}

interface FieldErrors {
    tareWeightError: string;
    boxNumberError: string;
    truckNumberError: string;
    chassisNumberError: string;
    sandTicketNumberError: string;
    grossWeightError: string;
    netWeightError: string;
    stageError: string;
    firstNameError: string;
    lastNameError: string;
    signatureError: string;
    emptyBoxNumber1Error: string;
    emptyBoxNumber2Error: string;
    mainError: string;
}

const initialErrorsState: FieldErrors = {
    tareWeightError: '',
    boxNumberError: '',
    truckNumberError: '',
    chassisNumberError: '',
    sandTicketNumberError: '',
    grossWeightError: '',
    netWeightError: '',
    stageError: '',
    firstNameError: '',
    lastNameError: '',
    signatureError: '',
    emptyBoxNumber1Error: '',
    emptyBoxNumber2Error: '',
    mainError: '',
};

const useEditLoadFieldOpForm = (loadDetailData: any, loadProgressData1: any): any => {
    const [inputs, setInputs] = useState({
        loadId: loadDetailData.loadId,
        jobId: loadDetailData.loadJobId,
        chassisId: loadDetailData.chassisId ? loadDetailData.chassisId : 0,
        chassisNumber: loadDetailData.chassisNumber ? loadDetailData.chassisNumber : '', //string
        truckNumber: loadDetailData.truckNumber ? loadDetailData.truckNumber : '', // string
        boxNumber: loadDetailData.boxNumber ? loadDetailData.boxNumber : '', // string
        boxId: loadDetailData.boxId ? loadDetailData.boxId : 0,
        sandTicketNumber: loadProgressData1?.sandTicketNumber ? loadProgressData1?.sandTicketNumber : '', // string
        grossWeight: loadProgressData1?.grossWeight | 0,
        netWeight: loadProgressData1?.weight | 0,
        tareWeight: loadProgressData1?.tareWeight | 0,
        stageId: 0,
        firstName: '',
        lastName: '',
        emptyBoxNumber1: '',
        emptyBoxNumber1Id: 0,
        emptyBoxCount: 0,
        emptyBoxNumber2: '',
        emptyBoxNumber2Id: 0,
        signatureBase64: '',
    });

    const alphanumeric = /^[a-z0-9-]+$/i;
    const numeric = /^[0-9]*$/;
    const WEIGHT_NUMERIC_ERROR = 'Weight must be numeric';
    const WEIGHT_NUMERIC_RANGES_ERROR = 'Weight must be numeric and between 1000 and 65000.';
    const SAND_TICKET_TWENTY_FIVE_CHARS_ERROR = 'Sand Ticket should be 25 or less alphanumeric characters.';

    const [fieldErrors, setFieldErrors] = useState(initialErrorsState);
    const [disable, setDisable] = useState(true);
    const [signState, setSignState] = useState(0);

    const [updateLoadFieldOp, { loading: mutationFieldLoading }] = useMutation<UpdateLoadFieldOpDetail>(UPDATE_LOAD_FIELD_OP, {
        onCompleted: (data) => {
            const updateLoadFieldErrors = data.updateLoadFieldOpDetail?.errors;
            if (updateLoadFieldErrors && updateLoadFieldErrors.length > 0) {
                setFieldErrors({
                    ...fieldErrors,
                    mainError: 'Error: ' + (updateLoadFieldErrors.filter((e): e is string => e != null)[0] ?? ''),
                });
            } else {
                const uploadImageToLoadInputDetails: UploadImageToLoadInputDetails = {
                    imageName: `Original-Load-${inputs.loadId}-Bol.jpeg`,
                    imageData: inputs.signatureBase64,
                    name: 'sign',
                    isDriver: false,
                };
    
                const uploadImageToLoadInput: UploadImageToLoadInput = {
                    loadId: inputs.loadId,
                    uploadImageToLoad: uploadImageToLoadInputDetails,
                };
                
                uploadImageMutation({
                    variables: { input: uploadImageToLoadInput },
                });
            
                
            }
        },
        onError: (e) => {
            if (CombinedGraphQLErrors.is(e)) {
                console.log('GraphQL Error', e.errors);
            } else {
                console.log('Error', e.message);
            }
        },
    });
    const [uploadImageMutation, { loading: mutationLoading }] = useMutation(UPLOAD_IMAGE_TO_LOAD, {
        onCompleted: data => {
            window.location.reload();
        },
        onError: (e) => {
            if (CombinedGraphQLErrors.is(e)) {
                console.log('GraphQL Error', e.errors);
            } else {
                console.log('Error', e.message);
            }
        },
    });
    const validateWeight = (value: any): void => {
        const weight = value ? value.toString() : '';

        if (weight.length === 0) {
            setFieldErrors({ ...fieldErrors, netWeightError: '' });

            return;
        }

        if (!numeric.test(weight)) {
            setFieldErrors({ ...fieldErrors, netWeightError: WEIGHT_NUMERIC_RANGES_ERROR });

            return;
        }

        const numWeight = parseInt(weight);

        if (numWeight < 1000 || numWeight > 65000) {
            setFieldErrors({ ...fieldErrors, netWeightError: WEIGHT_NUMERIC_RANGES_ERROR });
        } else {
            setFieldErrors({ ...fieldErrors, netWeightError: '' });
        }
    };

    const checkEmptyBox = (name: any): boolean => {
        if(loadDetailData.isBellyDump){
            return true;
        }
        let emp = false;
        if (inputs.emptyBoxCount === 0 || name === 'emptyBoxNumber0') {
            return true;
        } else if (inputs.emptyBoxCount == 1) {
            if (name === 'emptyBoxNumber1') {
                return true;
            }
            emp = inputs.emptyBoxNumber1Id !== 0;
        } else if (inputs.emptyBoxCount == 2) {
            if (name === 'emptyBoxNumber2') {
                return true;
            }
            emp = inputs.emptyBoxNumber1Id !== 0 && inputs.emptyBoxNumber2Id !== 0;
        }
        return emp;
    };
    const validateRequiredFields = (): any => {
        let tareWeightError = '';
        let boxNumberError = '';
        let truckNumberError = '';
        let chassisNumberError = '';
        let sandTicketNumberError = '';
        let grossWeightError = '';
        let netWeightError = '';
        const stageError = '';
        let firstNameError = '';
        let lastNameError = '';
        let signatureError = '';
        let emptyBoxNumber1Error = '';
        let emptyBoxNumber2Error = '';
        const requiredFieldMessage = 'This field is required.';
        if (signState == 0) {
            signatureError = 'Signature Required';
        }
        if ((inputs.chassisNumber === 0 || inputs.chassisNumber === '') && !loadDetailData.isBellyDump) {
            chassisNumberError = requiredFieldMessage;
        }

        if (inputs.truckNumber === 0 || inputs.truckNumber.toString() === '') {
            truckNumberError = 'Value should not be 0. Please enter valid Truck Number.';
        }
        if ((inputs.boxNumber === 0 || inputs.boxNumber.toString() === '') && !loadDetailData.isBellyDump) {
            boxNumberError = requiredFieldMessage;
        }

        if ((inputs.boxId === 0 || inputs.boxId.toString() === '') && !loadDetailData.isBellyDump) {
            boxNumberError = 'Selected Box doesnt exists';
        }

        if (inputs.sandTicketNumber === 0 || inputs.sandTicketNumber.trim() === '') {
            sandTicketNumberError = requiredFieldMessage;
        }

        if (inputs.grossWeight == 0 || inputs.grossWeight.toString() == '') {
            grossWeightError = requiredFieldMessage;
        }

        if (inputs.netWeight === 0 || inputs.netWeight.toString() == '') {
            netWeightError = requiredFieldMessage;
        }

        if (inputs.tareWeight === 0 || inputs.tareWeight.toString() == '') {
            tareWeightError = requiredFieldMessage;
        }
        // if (inputs.stageId === 0 || inputs.stageId.toString() === '') {
        //     stageError = requiredFieldMessage;
        // }
        if (inputs.firstName === '') {
            firstNameError = requiredFieldMessage;
        }
        if (inputs.lastName === '') {
            lastNameError = requiredFieldMessage;
        }
        if (inputs.emptyBoxCount == 1) {
            if (inputs.emptyBoxNumber1Id == 0) {
                emptyBoxNumber1Error = requiredFieldMessage;
            }
        }
        if (inputs.emptyBoxCount == 2) {
            if (inputs.emptyBoxNumber1Id == 0) {
                emptyBoxNumber1Error = requiredFieldMessage;
            }
            if (inputs.emptyBoxNumber2Id == 0) {
                emptyBoxNumber2Error = requiredFieldMessage;
            }
            if (inputs.emptyBoxNumber1Id == inputs.emptyBoxNumber2Id) {
                emptyBoxNumber2Error = 'Cannot be same as Empty Box 1';
            }
        }
        return {
            chassisNumberError,
            truckNumberError,
            boxNumberError,
            sandTicketNumberError,
            grossWeightError,
            netWeightError,
            tareWeightError,
            stageError,
            firstNameError,
            lastNameError,
            signatureError,
            emptyBoxNumber1Error,
            emptyBoxNumber2Error,
        };
    };

    const checkForRequiredFields = (name: any, value?: any): boolean => {
        if (
            (name === 'firstName' ? '' : inputs.firstName === '') ||
            (name === 'lastName' ? '' : inputs.lastName === '') ||
            (name === 'sandTicketNumber' ? '' : inputs.sandTicketNumber === '') ||
            inputs.truckNumber === '' ||
            (inputs.chassisId === 0 && !loadDetailData.isBellyDump)||
            (inputs.boxId === 0 && !loadDetailData.isBellyDump) ||
            //inputs.stageId === 0 ||
            (name === 'grossWeight' ? false : inputs.grossWeight === 0) ||
            (name === 'netWeight' ? false : inputs.netWeight === 0) ||
            (name === 'tareWeight' ? false : inputs.tareWeight === 0) ||
            (name === 'isSignEmpty' ? false : signState === 0) ||
            !checkEmptyBox(name) || fieldErrors.netWeightError!=='' || fieldErrors.grossWeightError !=='' ||
            fieldErrors.tareWeightError!=='' || fieldErrors.sandTicketNumberError !== ''
        ) {
            return false;
        }
        return true;
    };

    const handleSignature = (isEmpty: any, base64: string): void => {
        if (isEmpty) {
            setSignState(0);
            setFieldErrors({
                ...fieldErrors,
                signatureError: 'Singature Required',
            });
            setDisable(true);
        } else {
            setSignState(1);
            setInputs({
                ...inputs,
                signatureBase64: base64,
            });
            setDisable(false);
            checkForRequiredFields('isSignEmpty', false) ? setDisable(false) : setDisable(true);
        }
    };
    const handleDropdownChange = (fieldName: string, newValue: string): void => {
        setInputs(inputs => ({ ...inputs, [fieldName]: +newValue }));
        checkForRequiredFields(fieldName, +newValue) ? setDisable(false) : setDisable(true);
    };

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): void => {
        event.persist();
        setInputs(inputs => ({ ...inputs, [event.target.name]: event.target.value }));
        checkForRequiredFields(event.target.name) ? setDisable(false) : setDisable(true);
    };

    const handleInputChangeForRequiredFields = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): void => {
        event.persist();
        setInputs(inputs => ({ ...inputs, [event.target.name]: event.target.value.trim() }));
        if (event.target.value === null || event.target.value.trim() === '' || parseInt(event.target.value) == 0) {
            if (event.target.name == 'firstName') {
                setFieldErrors({
                    ...fieldErrors,
                    firstNameError: 'First Name is Required',
                });
            } else if (event.target.name == 'lastName') {
                setFieldErrors({
                    ...fieldErrors,
                    lastNameError: 'Last Name is Required',
                });
            } else if (event.target.name == 'sandTicketNumber') {
                setFieldErrors({
                    ...fieldErrors,
                    sandTicketNumberError: 'This Field is Required',
                });
            } else if (event.target.name == 'truckNumber') {
                setFieldErrors({
                    ...fieldErrors,
                    truckNumberError: 'This Field is Required',
                });
            } else if (event.target.name == 'grossWeight') {
                setFieldErrors({
                    ...fieldErrors,
                    grossWeightError: 'This Field is Required',
                });
            } else if (event.target.name == 'netWeight') {
                setFieldErrors({
                    ...fieldErrors,
                    netWeightError: 'This Field is Required',
                });
            } else if (event.target.name == 'tareWeight') {
                setFieldErrors({
                    ...fieldErrors,
                    tareWeightError: 'This Field is Required',
                });
            }
            setDisable(true);
        } else {
            if (event.target.name == 'sandTicketNumber') {
                const sandTicket = event.target.value ? event.target.value.toString() : '';
                if (sandTicket.length > 25 || !alphanumeric.test(sandTicket)) {
                    setFieldErrors({ ...fieldErrors, sandTicketNumberError: SAND_TICKET_TWENTY_FIVE_CHARS_ERROR });
                    setDisable(true);
                    return;
                } else {
                    setFieldErrors({
                        ...fieldErrors,
                        sandTicketNumberError: '',
                    });
                    setDisable(false);
                    return;
                }
            } else if (event.target.name == 'truckNumber') {
                setFieldErrors({
                    ...fieldErrors,
                    truckNumberError: '',
                });
            } else if (event.target.name == 'grossWeight') {
                const grossWeight = event.target.value ? event.target.value.toString() : '';

                if (!numeric.test(grossWeight)) {
                    setFieldErrors({ ...fieldErrors, grossWeightError: WEIGHT_NUMERIC_ERROR });
                    setDisable(true);
                    return;
                } else {
                    setFieldErrors({ ...fieldErrors, grossWeightError: '' });
                    setDisable(false);
                    return;
                }
            } else if (event.target.name == 'tareWeight') {
                const tareWeight = event.target.value ? event.target.value.toString() : '';

                if (!numeric.test(tareWeight)) {
                    setFieldErrors({ ...fieldErrors, tareWeightError: WEIGHT_NUMERIC_ERROR });
                    setDisable(true);
                    return;
                } else {
                    setFieldErrors({ ...fieldErrors, tareWeightError: '' });
                    setDisable(false);
                    return;
                }
            } else if (event.target.name == 'netWeight') {
                const weight = event.target.value ? event.target.value.toString() : '';
                if (weight.length === 0) {
                    setFieldErrors({ ...fieldErrors, netWeightError: '' });
                    return;
                }
                if (!numeric.test(weight)) {
                    setFieldErrors({ ...fieldErrors, netWeightError: WEIGHT_NUMERIC_RANGES_ERROR });
                    return;
                }
                const numWeight = parseInt(weight);

                if (numWeight < 1000 || numWeight > 65000) {
                    setFieldErrors({ ...fieldErrors, netWeightError: WEIGHT_NUMERIC_RANGES_ERROR });
                    setDisable(true);
                    return;
                } else {
                    setFieldErrors({ ...fieldErrors, netWeightError: '' });
                    setDisable(false);
                    return;
                }
            } else if (event.target.name == 'firstName') {
                setFieldErrors({
                    ...fieldErrors,
                    firstNameError: '',
                });
            } else if (event.target.name == 'lastName') {
                setFieldErrors({
                    ...fieldErrors,
                    lastNameError: '',
                });
            }
            checkForRequiredFields(event.target.name) ? setDisable(false) : setDisable(true);
        }
    };

    const handleEmptyBoxes = (input: number): void => {
        if (input == 0) {
            setInputs({
                ...inputs,
                emptyBoxNumber1: '',
                emptyBoxNumber2: '',
                emptyBoxNumber1Id: 0,
                emptyBoxNumber2Id: 0,
                emptyBoxCount: 0,
            });
            setFieldErrors({
                ...fieldErrors,
                emptyBoxNumber1Error: '',
                emptyBoxNumber2Error: '',
            });
            checkForRequiredFields('emptyBoxNumber0', +0) ? setDisable(false) : setDisable(true);
        }
        if (input == 1) {
            setInputs({
                ...inputs,
                emptyBoxNumber2: '',
                emptyBoxNumber2Id: 0,
                emptyBoxCount: 1,
            });
            setFieldErrors({
                ...fieldErrors,
                emptyBoxNumber2Error: '',
            });
            if (inputs.emptyBoxNumber1Id != 0) {
                checkForRequiredFields('emptyBoxNumber2', +0) ? setDisable(false) : setDisable(true);
            } else {
                setDisable(true);
            }
        }
        if (input == 2) {
            setInputs({
                ...inputs,
                emptyBoxCount: 2,
            });
            setDisable(true);
        }
    };

    const handleAutoCompleteChange = (inputName: string, value: Suggestion): void => {
        switch (inputName) {
            case 'boxNumber': {
                setInputs({
                    ...inputs,
                    boxId: parseInt(value.id.toString()),
                });
                if (value.name !== '' && +value.id === 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        boxNumberError: 'No Box Number found.',
                    });
                } else if (value.name === '' || (value.name !== '' && +value.id !== 0)) {
                    setFieldErrors({
                        ...fieldErrors,
                        boxNumberError: '',
                    });
                }
                if (
                    +value.id !== 0 &&
                    value.name !== '' &&
                    inputs.chassisId !== 0 &&
                    //inputs.stageId !== 0 &&
                    inputs.grossWeight !== 0 &&
                    inputs.tareWeight !== 0 &&
                    inputs.netWeight !== 0 &&
                    inputs.sandTicketNumber.trim() !== '' &&
                    inputs.truckNumber.trim() !== '' &&
                    signState == 1 &&
                    inputs.firstName !== '' &&
                    inputs.lastName !== ''
                ) {
                    setDisable(false);
                } else {
                    setDisable(true);
                }
                break;
            }
            case 'chassisNumber': {
                setInputs({
                    ...inputs,
                    chassisId: parseInt(value.id.toString()),
                    chassisNumber: value.name,
                });
                if (value.name !== '' && +value.id === 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        chassisNumberError: 'No Chassis Number found.',
                    });
                } else if (value.name === '' || (value.name !== '' && +value.id !== 0)) {
                    setFieldErrors({
                        ...fieldErrors,
                        chassisNumberError: '',
                    });
                }
                if (
                    +value.id !== 0 &&
                    inputs.boxId !== 0 &&
                    //inputs.stageId !== 0 &&
                    inputs.grossWeight !== 0 &&
                    inputs.tareWeight !== 0 &&
                    inputs.netWeight !== 0 &&
                    inputs.sandTicketNumber !== '' &&
                    inputs.truckNumber !== '' &&
                    signState == 1 &&
                    inputs.firstName !== '' &&
                    inputs.lastName !== ''
                ) {
                    setDisable(false);
                } else {
                    setDisable(true);
                }
                break;
            }
            case 'stage': {
                setInputs({
                    ...inputs,
                    stageId: parseInt(value.id.toString()),
                });
                if (value.name !== '' && +value.id === 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        stageError: '',
                    });
                } else if (value.name === '' || (value.name !== '' && +value.id !== 0)) {
                    setFieldErrors({
                        ...fieldErrors,
                        stageError: '',
                    });
                }
                if (
                    inputs.boxId !== 0 &&
                    inputs.chassisId !== 0 &&
                    inputs.grossWeight !== 0 &&
                    inputs.tareWeight !== 0 &&
                    inputs.netWeight !== 0 &&
                    inputs.sandTicketNumber !== '' &&
                    inputs.truckNumber !== '' &&
                    signState == 1 &&
                    inputs.firstName !== '' &&
                    inputs.lastName !== ''
                ) {
                    setDisable(false);
                } else {
                    setDisable(true);
                }
                break;
            }
            case 'emptyBoxNumber1': {
                setInputs({
                    ...inputs,
                    emptyBoxNumber1Id: +value.id,
                });
                if (value.name !== '' && +value.id === 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        emptyBoxNumber1Error: 'No Empty Box found.',
                    });
                } else if (value.name === '' || (value.name !== '' && +value.id !== 0)) {
                    setFieldErrors({
                        ...fieldErrors,
                        emptyBoxNumber1Error: '',
                    });
                }

                if (+value.id != 0 && inputs.emptyBoxNumber2Id == value.id) {
                    setFieldErrors({
                        ...fieldErrors,
                        emptyBoxNumber1Error: 'Cannot Select Same as Empty Box2',
                    });
                }
                const isEmptybox2 = inputs.emptyBoxCount == 2 ? inputs.emptyBoxNumber2Id != 0 : +value.id !== 0;
                if (
                    +value.id !== 0 &&
                    inputs.boxId !== 0 &&
                    inputs.chassisId !== 0 &&
                    inputs.grossWeight !== 0 &&
                    //inputs.stageId !== 0 &&
                    inputs.tareWeight !== 0 &&
                    inputs.netWeight !== 0 &&
                    inputs.sandTicketNumber !== '' &&
                    inputs.truckNumber !== '' &&
                    signState == 1 &&
                    inputs.firstName !== '' &&
                    inputs.lastName !== '' &&
                    isEmptybox2
                ) {
                    setDisable(false);
                } else {
                    setDisable(true);
                }
                break;
            }
            case 'emptyBoxNumber2': {
                setInputs({
                    ...inputs,
                    emptyBoxNumber2Id: +value.id,
                });
                if (value.name !== '' && +value.id === 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        emptyBoxNumber2Error: 'No Empty Box found.',
                    });
                } else if (value.name === '' || (value.name !== '' && +value.id !== 0)) {
                    setFieldErrors({
                        ...fieldErrors,
                        emptyBoxNumber2Error: '',
                    });
                }
                if (+value.id != 0 && inputs.emptyBoxNumber1Id != 0 && inputs.emptyBoxNumber1Id == value.id) {
                    setFieldErrors({
                        ...fieldErrors,
                        emptyBoxNumber2Error: 'Cannot Select Same as Empty Box1',
                    });
                }
                if (
                    +value.id !== 0 &&
                    inputs.emptyBoxNumber1Id != value.id &&
                    inputs.boxId !== 0 &&
                    inputs.chassisId !== 0 &&
                    inputs.grossWeight !== 0 &&
                    inputs.tareWeight !== 0 &&
                    inputs.netWeight !== 0 &&
                    inputs.sandTicketNumber !== '' &&
                    inputs.truckNumber !== '' &&
                    signState == 1 &&
                    inputs.firstName !== '' &&
                    inputs.lastName !== ''
                ) {
                    setDisable(false);
                } else {
                    setDisable(true);
                }
                break;
            }
        }
    };

    const handleSubmit = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): void => {
        if (event) {
            event.preventDefault();
        }
        const {
            tareWeightError,
            boxNumberError,
            truckNumberError,
            chassisNumberError,
            sandTicketNumberError,
            grossWeightError,
            netWeightError,
            //stageError,
            firstNameError,
            lastNameError,
            signatureError,
            emptyBoxNumber1Error,
            emptyBoxNumber2Error,
        } = validateRequiredFields();

        setFieldErrors({
            ...fieldErrors,
            tareWeightError,
            boxNumberError,
            truckNumberError,
            chassisNumberError,
            grossWeightError,
            netWeightError,
            sandTicketNumberError,
            //stageError,
            firstNameError,
            lastNameError,
            signatureError,
            emptyBoxNumber1Error,
            emptyBoxNumber2Error,
        });
        if (
            tareWeightError === '' &&
            boxNumberError === '' &&
            truckNumberError === '' &&
            chassisNumberError === '' &&
            grossWeightError === '' &&
            netWeightError === '' &&
            sandTicketNumberError === '' &&
            //stageError === '' &&
            firstNameError === '' &&
            lastNameError === '' &&
            signatureError === '' &&
            emptyBoxNumber1Error === '' &&
            emptyBoxNumber2Error === ''
        ) {
            const updateLoadOpInput = {
                boxId: inputs.boxId,
                chassisId: inputs.chassisId,
                truckNumber: inputs.truckNumber,
                sandTicketNumber: inputs.sandTicketNumber,
                grossWeight: parseInt(inputs.grossWeight.toString()),
                netWeight: parseInt(inputs.netWeight.toString()),
                tareWeight: parseInt(inputs.tareWeight.toString()),
                stageId: inputs.stageId,
                signatureBase64: inputs.signatureBase64,
                emptyBoxNumber1Id: inputs.emptyBoxNumber1Id ? inputs.emptyBoxNumber1Id : 0,
                emptyBoxNumber2Id: inputs.emptyBoxNumber2Id ? inputs.emptyBoxNumber2Id : 0,
                firstName: inputs.firstName,
                lastName: inputs.lastName,
            };

            const LoadDetailFieldOpEdit = {
                loadId: inputs.loadId,
                jobId: inputs.jobId,
                loadDetailFieldOpValues: updateLoadOpInput,
            };
            updateLoadFieldOp({
                variables: { input: LoadDetailFieldOpEdit },
            });

         
        }
    };

    return {
        handleSubmit,
        handleDropdownChange,
        handleAutoCompleteChange,
        handleSignature,
        handleInputChange,
        inputs,
        fieldErrors,
        validateRequiredFields,
        disable,
        handleInputChangeForRequiredFields,
        handleEmptyBoxes,
        mutationFieldLoading,
    };
};

export default useEditLoadFieldOpForm;
